
import { Component, Vue, Watch } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { API_TSponsorshipCandidate } from "@/models/company_sponsorship_candidates";

import { ERROR_NOTIFICATION_TYPE, NOTIFICATION_PROPS_LOCK } from "@/constants/notification";
import { WARNING_POPUP_TYPE } from "@/constants/popupTypes";
import { SMUser, SMCases } from "@/store";
import { CFormField, validateFormFields } from "@/models/form_cards";
import { STEPS_FOR_EDITING, EXPERT_REVIEW_SUBMISSION_SERVICE_STEP } from "@/constants/services";

const FORM_MODEL = {
  first_name: "",
  last_name: "",
  email: "",
};

const EMAIL_ALIAS = "email";
const EMAIL_INPUT_MASK = "^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+.[a-zA-Z0-9-.]+$";

@Component({
  components: {
    NotificationTemplate: () =>
      import("@/components/NotificationTemplate/NotificationTemplate.vue"),
  },
})
export default class AppendSponsorshipCandidate extends Vue {
  form: API_TSponsorshipCandidate = FORM_MODEL;

  EMAIL_INPUT_MASK = EMAIL_INPUT_MASK;

  warningPopupType = WARNING_POPUP_TYPE;

  loading = false;

  get caseId() {
    return Number(this.$route.params?.id ?? 0);
  }

  appendedCandidate = null;

  get taskIdForAppendedCandidate() {
    return this.appendedCandidate?.task_id ?? null;
  }

  async addCandidate() {
    const notification = {};

    try {
      this.loading = true;

      this.form.first_name = this.form.first_name.trim();
      this.form.last_name = this.form.last_name.trim();
      this.form.email = this.form.email.trim();

      const { data } = await this.axios.post(
        API_ROUTES.COMPANY_ADD_SPONSORSHIP_CANDIDATE(this.caseId),
        this.form
      );

      this.appendedCandidate = data;

      this.openContinueCandidatePopup();
    } catch (err) {
      Object.assign(notification, {
        type: ERROR_NOTIFICATION_TYPE,
        title: this.$tc("notification.error.title"),
        message: this.$tc("notification.error.message"),
      });

      this.$root.$emit("show-notification", notification);
    } finally {
      this.loading = false;

      Object.assign(this.form, {
        ...FORM_MODEL,
      });

      Object.keys(this.form).forEach((key) => {
        this.form[key] = null;
      });

      this.candidateEmail.value = "";

      const el = this.$refs.email;
      (el as any).showError?.(false);

      this.$emit("update");
    }
  }

  get caseOnRelevantStep() {
    if (SMUser.isExpert) return true;

    return STEPS_FOR_EDITING.includes(SMCases.caseStepName);
  }

  get disabled() {
    const { form } = this;

    const forIsNotValid =
      Object.keys(form).some((key) => !form[key] || form[key] === "") ||
      !this.isCandidateEmailValid;

    // const editingIsDisabled = !(SMCases.case as API_TCase).editing_enabled;
    const editingIsDisabled = !this.caseOnRelevantStep;

    return forIsNotValid || editingIsDisabled;
  }

  continueCandidatePopup = false;

  openContinueCandidatePopup() {
    if (!this.appendedCandidate?.id) return;

    this.continueCandidatePopup = true;
  }

  closeContinueCandidatePopup() {
    this.continueCandidatePopup = false;

    Object.assign(this.appendedCandidate, null);
  }

  continueCandidate() {
    const { taskIdForAppendedCandidate } = this;

    if (!taskIdForAppendedCandidate) return;

    const path = SMUser.isExpert
      ? `/cases/tasks/${taskIdForAppendedCandidate}`
      : `${SMUser.basePath}/tasks/${taskIdForAppendedCandidate}`;

    this.$router.push(path);
  }

  isEmailAlreadyRegistered = false;

  @Watch("candidateEmail.value")
  onUpdateCandidateEmailValue(val) {
    this.form.email = val;

    this.isEmailAlreadyRegistered = false;
  }

  candidateEmail = new CFormField(this.$tc("label.email") as string).addRuleByAlias(EMAIL_ALIAS);

  get isCandidateEmailValid() {
    return validateFormFields([this.candidateEmail]);
  }

  get notificationProps() {
    return NOTIFICATION_PROPS_LOCK;
  }

  get showEditingNotification() {
    return !this.caseOnRelevantStep;
  }

  get editNotificationLabel() {
    return [EXPERT_REVIEW_SUBMISSION_SERVICE_STEP].includes(SMCases.caseStepName)
      ? this.$tc("notification.editingDisabled.expertStep")
      : this.$tc("notification.editingDisabled.furtherStep");
  }
}
